import React, { useEffect, useRef, useState } from 'react';
import { STEP_TO_SECTION_MAP } from '../utils/applicationSteps';
import AgreementWorkflowSection from './AgreementWorkflowSection';
import InterviewSection from './InterviewSection';
import OfferLetterSection from './OfferLetterSection';
import DocumentUpload from './DocumentUpload';
import TPCESDSection from './TPCESDSection';
import TicketSection from './TicketSection';
import DepartureSection from './DepartureSection';
import ClearanceSection from './ClearanceSection';
import WorkflowTimeline from './WorkflowTimeline';
import CandidateDocumentsDisplay from './CandidateDocumentsDisplay';

/**
 * WorkflowSectionContainer Component
 * 
 * Conditionally renders the active workflow section based on currentStep.
 * Implements CSS transitions for smooth step changes and manages scroll position.
 * 
 * This component ensures only one workflow section is rendered at a time,
 * preserving all props and callbacks to maintain existing business logic.
 * 
 * @param {Object} props - Component props
 * @param {string} props.currentStep - The active step identifier
 * @param {Object} props.application - The application object
 * @param {Object} props.candidate - The candidate object
 * @param {string} props.userRole - The current user's role
 * @param {Function} props.onRefresh - Callback to refresh application data
 */
const WorkflowSectionContainer = ({
  currentStep,
  application,
  candidate,
  userRole,
  onRefresh
}) => {
  const containerRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayStep, setDisplayStep] = useState(currentStep);

  /**
   * Handle step transitions with fade effect
   * Implements a 300ms transition as specified in requirements
   */
  useEffect(() => {
    if (currentStep !== displayStep) {
      // Start fade out
      setIsTransitioning(true);

      // After fade out completes, update displayed step and fade in
      const timer = setTimeout(() => {
        setDisplayStep(currentStep);
        setIsTransitioning(false);

        // Reset scroll position to top
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
        // Also reset window scroll
        window.scrollTo(0, 0);
      }, 150); // Half of 300ms for fade out

      return () => clearTimeout(timer);
    }
  }, [currentStep, displayStep]);

  /**
   * Get the section component name for the current step
   */
  const getSectionName = () => {
    if (!displayStep) {
      return null;
    }
    return STEP_TO_SECTION_MAP[displayStep];
  };

  /**
   * Render the appropriate workflow section based on displayStep
   * Passes all necessary props to maintain existing functionality
   */
  const renderSection = () => {
    const sectionName = getSectionName();

    // Handle null sections (applied, shortlisted) - show nothing
    if (!sectionName) {
      return null;
    }

    // Handle edge cases
    if (!application) {
      return (
        <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <p className="text-text-secondary text-center">Loading application data...</p>
        </div>
      );
    }

    // Render the appropriate section component
    switch (sectionName) {
      case 'AgreementWorkflowSection':
        // Only show if agreement stage or applicationStatus exists
        if (application.currentStage === 'AGREEMENT' || application.applicationStatus) {
          return (
            <AgreementWorkflowSection
              application={application}
              userRole={userRole}
              onRefresh={onRefresh}
            />
          );
        }
        return null;

      case 'InterviewSection':
        return (
          <InterviewSection
            applicationId={application._id}
            applicationStage={application.currentStage}
            onInterviewUpdate={onRefresh}
          />
        );

      case 'OfferLetterSection':
        // Show for both offer and payment steps
        if (
          application.currentStage === 'OFFER_LETTER' ||
          application.currentStage === 'PAYMENT' ||
          application.offerLetter?.fileUrl
        ) {
          return (
            <OfferLetterSection
              application={application}
              candidate={candidate}
              userRole={userRole}
              onRefresh={onRefresh}
            />
          );
        }
        return null;

      case 'DocumentUpload':
        return (
          <DocumentUpload
            applicationId={application._id}
            application={application}
            currentStage={application.currentStage}
            onUploadSuccess={onRefresh}
          />
        );

      case 'TPCESDSection':
        return (
          <TPCESDSection
            application={application}
            userRole={userRole}
            onRefresh={onRefresh}
          />
        );

      case 'TicketSection':
        // Only show if ticket stage or ticket data exists
        if (application.currentStage === 'TICKET' || application.ticket?.fileUrl) {
          return (
            <TicketSection
              application={application}
              userRole={userRole}
              onRefresh={onRefresh}
            />
          );
        }
        return null;

      case 'ClearanceSection':
        // Show clearance section when in clearance stage
        if (application.currentStage === 'CLEARANCE' || application.clearance?.approvedAt) {
          return (
            <ClearanceSection
              application={application}
              userRole={userRole}
              onRefresh={onRefresh}
            />
          );
        }
        return null;

      case 'DepartureSection':
        // Only show if departure stage or departure data exists
        if (
          application.currentStage === 'DEPARTURE' ||
          application.departure?.videoUrl ||
          application.departure?.feedback ||
          application.departure?.notDepartedRemark
        ) {
          return (
            <DepartureSection
              application={application}
              userRole={userRole}
              onRefresh={onRefresh}
            />
          );
        }
        return null;

      case 'WorkflowTimeline':
        return <WorkflowTimeline currentStage={application.currentStage} />;

      default:
        console.warn(`Unknown section: ${sectionName}`);
        return null;
    }
  };

  const sectionName = getSectionName();

  return (
    <div
      ref={containerRef}
      className="workflow-section-container"
      style={{
        opacity: isTransitioning ? 0 : 1,
        transition: 'opacity 300ms ease-in-out'
      }}
    >
      {/* Always show Documents section for admin/HR users in workflow steps */}
      {candidate && sectionName && (
        <CandidateDocumentsDisplay candidate={candidate} compact={true} />
      )}
      
      {renderSection()}
    </div>
  );
};

export default WorkflowSectionContainer;
